<template>
    <div class="websearch-attribute-picker">
        <div class="filter-menu">
            <SupsisButton v-if="selectedMenu.length" ghost small @click="prev">
                <span class="material-icons"> chevron_left </span>
            </SupsisButton>
            <div class="breadcrumb-item" v-for="menu in selectedMenu" :key="menu">
                <span> {{ findAttributeByValue(menu).label }} ✦ </span>
            </div>
        </div>

        <div class="tavily-blocks" v-if="!selectedMenu.length">
            <div class="block-item" v-for="block in tavilyBlocks" :key="block.id" @click="selectBlock(block)">
                <span class="material-icons">travel_explore</span>
                <span class="name">{{ block.name || "Web Arama" }}</span>
            </div>
        </div>

        <div class="attribute-list" v-else>
            <div class="attr-select">
                <div
                    class="picker-item"
                    v-for="attr in filterAttributes()"
                    @click="() => select(attr.value)"
                    :key="attr.value"
                >
                    <span class="name">{{ attr.label }}</span>
                    <span class="description" v-if="attr.description">{{ attr.description }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";
import flatDiagram from "../../../utils/flatDiagram";

export default {
    name: "WebSearchAttributePicker",
    components: {
        SupsisButton,
    },
    props: {
        onPicked: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            searchText: "",
            attributes: [],
            selectedMenu: [],
            tavilyBlocks: [],
            selectedBlock: null,
        };
    },
    computed: {
        ...mapGetters({
            board: "CB/board",
        }),
    },
    methods: {
        listTavilyBlocks() {
            const storyId = this.$route.params.id;
            const board = this.board(storyId);
            const flatted = flatDiagram(board.diagram);
            return flatted.filter((block) => block.type === "TAVILY_WEB_SEARCH" || block.type === "OPENAI_WEB_SEARCH");
        },

        generateAttributesForBlock(block) {
            const blockId = block.id;

            if (block.type === "OPENAI_WEB_SEARCH") {
                return this.generateOpenAIWebSearchAttributes(blockId);
            }

            const basePrefix = `block.${blockId}`;

            const attributes = [
                {
                    label: "*",
                    value: `${basePrefix}.$..*`,
                    description: "Tüm değerler",
                },
                {
                    label: "Success",
                    value: `${basePrefix}.success`,
                    description: "Arama başarılı oldu mu?",
                },
                {
                    label: "Status",
                    value: `${basePrefix}.status`,
                    description: "HTTP durum kodu",
                },
                {
                    label: "Status Text",
                    value: `${basePrefix}.statusText`,
                    description: "HTTP durum mesajı",
                },
                {
                    label: "Data",
                    value: `${basePrefix}.data`,
                    description: "Tavily API yanıtı",
                    subAttributes: [
                        {
                            label: "*",
                            value: `${basePrefix}.data.$..*`,
                            description: "Tüm veri alanları",
                        },
                        {
                            label: "Sorgu",
                            value: `${basePrefix}.data.query`,
                            description: "Yapılan arama sorgusu",
                        },
                        {
                            label: "Takip Soruları",
                            value: `${basePrefix}.data.follow_up_questions`,
                            description: "Takip soruları (varsa)",
                        },
                        {
                            label: "Cevap",
                            value: `${basePrefix}.data.answer`,
                            description: "Tavily'nin ürettiği özet cevap",
                        },
                        {
                            label: "Yanıt Süresi",
                            value: `${basePrefix}.data.response_time`,
                            description: "API yanıt süresi (saniye)",
                        },
                        {
                            label: "Görseller",
                            value: `${basePrefix}.data.images`,
                            description: "Bulunan görseller (varsa)",
                        },
                        {
                            label: "Sonuçlar",
                            value: `${basePrefix}.data.results`,
                            description: "Arama sonuçları",
                            subAttributes: this.generateResultsAttributes(`${basePrefix}.data.results`),
                        },
                    ],
                },
                {
                    label: "Error",
                    value: `${basePrefix}.error`,
                    description: "Hata mesajı (varsa)",
                },
            ];

            return attributes;
        },

        generateOpenAIWebSearchAttributes(blockId) {
            const basePrefix = `block.${blockId}`;

            const attributes = [
                {
                    label: "*",
                    value: `${basePrefix}.$..*`,
                    description: "Tüm değerler",
                },
                {
                    label: "Success",
                    value: `${basePrefix}.success`,
                    description: "Arama başarılı oldu mu?",
                },
                {
                    label: "Status",
                    value: `${basePrefix}.status`,
                    description: "HTTP durum kodu",
                },
                {
                    label: "Status Text",
                    value: `${basePrefix}.statusText`,
                    description: "HTTP durum mesajı",
                },
                {
                    label: "Data",
                    value: `${basePrefix}.data`,
                    description: "OpenAI Web Arama yanıtı",
                    subAttributes: [
                        {
                            label: "*",
                            value: `${basePrefix}.data.$..*`,
                            description: "Tüm veri alanları",
                        },
                        {
                            label: "ID",
                            value: `${basePrefix}.data.id`,
                            description: "Yanıt ID'si",
                        },
                        {
                            label: "Object",
                            value: `${basePrefix}.data.object`,
                            description: "Object türü",
                        },
                        {
                            label: "Oluşturulma Zamanı",
                            value: `${basePrefix}.data.created_at`,
                            description: "Yanıtın oluşturulma zamanı",
                        },
                        {
                            label: "Durum",
                            value: `${basePrefix}.data.status`,
                            description: "İşlem durumu",
                        },
                        {
                            label: "Model",
                            value: `${basePrefix}.data.model`,
                            description: "Kullanılan model",
                        },
                        {
                            label: "Çıktı",
                            value: `${basePrefix}.data.output`,
                            description: "Çıktı listesi",
                            subAttributes: this.generateOpenAIOutputAttributes(`${basePrefix}.data.output`),
                        },
                        {
                            label: "Sıcaklık",
                            value: `${basePrefix}.data.temperature`,
                            description: "Kullanılan sıcaklık değeri",
                        },
                        {
                            label: "Top P",
                            value: `${basePrefix}.data.top_p`,
                            description: "Top P değeri",
                        },
                        {
                            label: "Kullanım İstatistikleri",
                            value: `${basePrefix}.data.usage`,
                            description: "Token kullanım bilgileri",
                            subAttributes: [
                                {
                                    label: "Giriş Token",
                                    value: `${basePrefix}.data.usage.input_tokens`,
                                    description: "Kullanılan giriş token sayısı",
                                },
                                {
                                    label: "Çıkış Token",
                                    value: `${basePrefix}.data.usage.output_tokens`,
                                    description: "Kullanılan çıkış token sayısı",
                                },
                                {
                                    label: "Toplam Token",
                                    value: `${basePrefix}.data.usage.total_tokens`,
                                    description: "Kullanılan toplam token sayısı",
                                },
                            ],
                        },
                        {
                            label: "Çıktı Metni",
                            value: `${basePrefix}.data.output_text`,
                            description: "İşlenmiş çıktı metni",
                        },
                    ],
                },
                {
                    label: "Error",
                    value: `${basePrefix}.error`,
                    description: "Hata mesajı (varsa)",
                },
            ];

            return attributes;
        },

        generateOpenAIOutputAttributes(prefix) {
            return [
                {
                    label: "*",
                    value: `${prefix}.$..*`,
                    description: "Tüm çıktı alanları",
                },
                {
                    label: "Tür",
                    value: `${prefix}.type`,
                    description: "Çıktının türü",
                },
                {
                    label: "ID",
                    value: `${prefix}.id`,
                    description: "Çıktı ID'si",
                },
                {
                    label: "Durum",
                    value: `${prefix}.status`,
                    description: "Çıktı durumu",
                },
                {
                    label: "Rol",
                    value: `${prefix}.role`,
                    description: "Çıktı rolü (varsa)",
                },
                {
                    label: "İçerik",
                    value: `${prefix}.content`,
                    description: "İçerik listesi",
                    subAttributes: [
                        {
                            label: "İçerik Türü",
                            value: `${prefix}.content.type`,
                            description: "İçerik türü",
                        },
                        {
                            label: "Metin",
                            value: `${prefix}.content.text`,
                            description: "İçerik metni",
                        },
                        {
                            label: "Açıklamalar",
                            value: `${prefix}.content.annotations`,
                            description: "Metin açıklamaları/kaynaklar",
                            subAttributes: [
                                {
                                    label: "Tür",
                                    value: `${prefix}.content.annotations.type`,
                                    description: "Açıklama türü",
                                },
                                {
                                    label: "URL",
                                    value: `${prefix}.content.annotations.url`,
                                    description: "Kaynak URL'si",
                                },
                                {
                                    label: "Başlık",
                                    value: `${prefix}.content.annotations.title`,
                                    description: "Kaynak başlığı",
                                },
                            ],
                        },
                    ],
                },
            ];
        },

        generateResultsAttributes(prefix) {
            return [
                {
                    label: "*",
                    value: `${prefix}.$..*`,
                    description: "*",
                },

                {
                    label: "Başlık",
                    value: `${prefix}.title`,
                    description: "Sonucun başlığı",
                },
                {
                    label: "URL",
                    value: `${prefix}.url`,
                    description: "Sonucun URL'si",
                },
                {
                    label: "İçerik",
                    value: `${prefix}.content`,
                    description: "Sonucun içeriği",
                },
                {
                    label: "Ham İçerik",
                    value: `${prefix}.raw_content`,
                    description: "Sonucun ham içeriği (varsa)",
                },
                {
                    label: "Skor",
                    value: `${prefix}.score`,
                    description: "Sonucun alaka düzeyi skoru",
                },
            ];
        },

        selectBlock(block) {
            this.selectedBlock = block;
            this.attributes = this.generateAttributesForBlock(block);
            this.selectedMenu.push("root");
        },

        filterAttributes() {
            const parentAttribute = this.getParentAttribute();
            let attrs = parentAttribute ? parentAttribute.subAttributes : this.attributes;

            return attrs.filter((attr) => {
                if (this.searchText) {
                    return (attr.label || "").toLowerCase().includes(this.searchText.toLowerCase());
                }
                return true;
            });
        },

        getParentAttribute() {
            if (this.selectedMenu.length <= 1) return null;
            const attributes = this.getFlatAttributes();
            const parent = this.selectedMenu[this.selectedMenu.length - 1];
            return attributes.find((x) => x.value === parent);
        },

        getFlatAttributes() {
            const flat = (attr) => {
                if (attr.subAttributes) {
                    return [attr, ...attr.subAttributes.flatMap(flat)];
                }
                return [attr];
            };
            return this.attributes.flatMap(flat);
        },

        select(value) {
            const attributes = this.getFlatAttributes();
            const attr = attributes.find((x) => x.value === value);
            if (Array.isArray(attr.subAttributes) && attr.subAttributes.length) {
                this.selectedMenu.push(value);
            } else {
                this.onPicked(value);
            }
        },

        prev() {
            if (this.selectedMenu.length === 1) {
                this.selectedMenu = [];
                this.selectedBlock = null;
            } else {
                this.selectedMenu.pop();
            }
        },

        findAttributeByValue(value) {
            if (value === "root") {
                return { label: this.selectedBlock?.name || "Web Arama" };
            }
            const attributes = this.getFlatAttributes();
            return attributes.find((x) => x.value === value);
        },
    },
    created() {
        this.tavilyBlocks = this.listTavilyBlocks();
    },
};
</script>

<style lang="scss" scoped>
.websearch-attribute-picker {
    background: #fff;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    max-height: 600px;
    min-height: 200px;
    user-select: none;
    width: 100%;
    padding: 5px;

    .filter-menu {
        display: flex;
        align-items: center;
        gap: 3px;
        margin-bottom: 10px;

        .supsis-button {
            border: none !important;
            font-size: 13px;
            height: 25px;
        }

        .breadcrumb-item {
            display: flex;
        }
    }

    .tavily-blocks {
        .block-item {
            display: flex;
            align-items: center;
            padding: 8px;
            cursor: pointer;
            border-radius: 4px;
            margin-bottom: 4px;

            &:hover {
                background-color: #f0f4f7;
            }

            .material-icons {
                margin-right: 8px;
                color: #647283;
            }

            .name {
                color: #22272d;
                font-size: 14px;
            }
        }
    }

    .attribute-list {
        flex-grow: 1;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 5px;
        margin: 0px 5px 0px 5px;

        .picker-item {
            align-items: center;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            padding: 8px;
            transition: background-color 0.2s;
            border-radius: 4px;

            &:hover {
                background-color: #f0f4f7;
            }

            .name {
                color: #22272d;
                font-size: 14px;
            }

            .description {
                color: #9ba6b3;
                font-size: 12px;
            }
        }
    }
}
</style>
